import { Student, studentsProvider } from "./studentsProvider";
import { GetListParams, GetListResult } from "react-admin";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { applyPagination, applySort } from "../backoffice.utils";
import { drivingSchoolsProvider } from "./drivingSchoolsProvider";

type Cents = number;

type InvoiceCustomer = Student & {
  sumUnpaidInvoices: Cents;
  sumPaidInvoices?: Cents;
  remainingBudget?: Cents;
};

class InvoiceCustomersProvider extends AbstractDataProvider<InvoiceCustomer> {
  async getList(
    _resource: string,
    { filter: filter_, sort, pagination }: GetListParams,
  ): Promise<GetListResult<InvoiceCustomer>> {
    const { includeArchivedStudents, ...filter } = filter_;
    if (!filter.drivingSchoolId) {
      // Only load invoice customers of active driving schools ...
      const allDrivingSchools = [...drivingSchoolsProvider._recordsById!.values()];
      const activeDrivingSchools = allDrivingSchools.filter((it) => it.isActive);
      filter.drivingSchoolIds = activeDrivingSchools.map((it) => it.id);
    }
    if (!includeArchivedStudents) {
      filter.archived = false;
    }
    if (sort.field === "remainingBudget") {
      // Calculate remaining budgets before sorting and pagination.
      const { data: students, total } = await studentsProvider.getList("students", {
        filter: { ...filter, paymentStrategy: "purchaseOnAccount" },
        sort: { field: "id", order: "ASC" },
        pagination: { page: 1, perPage: 9999 },
      });
      if (typeof total !== "number") {
        throw new Error('Result of studentsProvider.getList("students", ...) did not contain total');
      }
      if (total > 9999) {
        throw new Error(`Cannot sort by ${sort.field} -- too many invoice customers`);
      }
      const data = students.map(_studentToInvoiceCustomer);
      return applyPagination(applySort(data, sort), pagination);
    } else {
      const field = { sumUnpaidInvoices: "balances.open", sumPaidInvoices: "balances.paid" }[sort.field] ?? sort.field;
      const { data: students, total } = await studentsProvider.getList("students", {
        filter: { ...filter, paymentStrategy: "purchaseOnAccount" },
        sort: { ...sort, field },
        pagination,
      });
      const data = students.map(_studentToInvoiceCustomer);
      return { data, total };
    }
  }
}

export const invoiceCustomersProvider = new InvoiceCustomersProvider();

function _studentToInvoiceCustomer(student: Student): InvoiceCustomer {
  const { open: sumUnpaidInvoices, paid: sumPaidInvoices } = student.balances;
  return {
    ...student,
    sumPaidInvoices,
    sumUnpaidInvoices,
    remainingBudget: sumPaidInvoices === undefined ? undefined : student.budget - sumPaidInvoices - sumUnpaidInvoices,
  };
}
